/**
 * DomainHeatMap — mapa de calor por dominio.
 *
 * Componente compartido entre Portal.tsx (vista post-cierre del auditor)
 * y CaptureResult.tsx (vista post-submit del cliente). ADR-041 D2.
 *
 * La logica de scoreColor vive aqui para no duplicarla entre ambas vistas.
 */

export function scoreColor(score: number): string {
  if (score >= 75) return '#16a34a';
  if (score >= 50) return '#ca8a04';
  return '#dc2626';
}

interface DomainEntry {
  /** Etiqueta que se mostrara en la barra (dominio o "codigo · dominio"). */
  label: string;
  score: number;
}

interface DomainHeatMapProps {
  domains: DomainEntry[];
}

/**
 * Renderiza una lista de barras de progreso coloreadas segun el score.
 * Acepta un array ya normalizado de {label, score} para desacoplar la
 * estructura de datos de Portal (instancias con per_domain) de la de
 * CaptureResult (per_domain plano).
 */
export function DomainHeatMap({ domains }: DomainHeatMapProps): JSX.Element {
  if (domains.length === 0) {
    return (
      <p className="text-xs text-cumple-gray-500">No hay dominios disponibles en esta plantilla.</p>
    );
  }
  return (
    <ul className="space-y-2" aria-label="Mapa de calor por dominio">
      {domains.map(({ label, score }) => (
        <li key={label} className="text-xs">
          <div className="flex justify-between mb-1">
            <span className="text-cumple-gray-700">{label}</span>
            <span className="font-semibold" style={{ color: scoreColor(score) }}>
              {Math.round(score)}/100
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-cumple-gray-200" role="presentation">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${String(Math.max(0, Math.min(100, score)))}%`,
                background: scoreColor(score),
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
